import React from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, useLocation, Navigate } from "react-router-dom";
import { AuthProvider, useAuth } from "./context/AuthContext";
import { SessionProvider } from "./context/SessionContext";
import { ThemeProvider } from "./context/ThemeContext";
import { LanguageProvider } from "./context/LanguageContext";
import { Toaster } from "sonner";

// Pages
import LandingPage from "./pages/LandingPage";
import LoginPage from "./pages/LoginPage";
import ResetPasswordPage from "./pages/ResetPasswordPage";
import Dashboard from "./pages/Dashboard";
import AgendaPage from "./pages/AgendaPage";
import SessionsPage from "./pages/SessionsPage";
import ElearningDashboard from "./pages/ElearningDashboard";
import CourseCatalog from "./pages/CourseCatalog";
import CourseDetail from "./pages/CourseDetail";
import QuizPage from "./pages/QuizPage";
import CertificatesPage from "./pages/CertificatesPage";
import PlaceholderPage from "./pages/PlaceholderPage";

// Data pages
import EntreprisesPage from "./pages/data/EntreprisesPage";
import EntrepriseDetailPage from "./pages/data/EntrepriseDetailPage";
import ApprenantsPage from "./pages/data/ApprenantsPage";
import ApprenantDetailPage from "./pages/data/ApprenantDetailPage";
import ApprenantsImportPage from "./pages/data/ApprenantsImportPage";
import IntervenantsPage from "./pages/data/IntervenantsPage";
import IntervenantDetailPage from "./pages/data/IntervenantDetailPage";
import IntervenantsImportPage from "./pages/data/IntervenantsImportPage";
import FinanceursPage from "./pages/data/FinanceursPage";
import FinanceurDetailPage from "./pages/data/FinanceurDetailPage";
import LieuxPage from "./pages/data/LieuxPage";
import LieuDetailPage from "./pages/data/LieuDetailPage";
import DonneesDashboardPage from "./pages/data/DonneesDashboardPage";

// Configuration pages
import ComptesAccesPage from "./pages/configuration/ComptesAccesPage";
import CompteAccesDetailPage from "./pages/configuration/CompteAccesDetailPage";
import ModelesDocumentsPage from "./pages/configuration/ModelesDocumentsPage";
import ModeleDetailPage from "./pages/configuration/ModeleDetailPage";
import AuditLogPage from "./pages/configuration/AuditLogPage";

// Commercial pages
import TunnelVentePage from "./pages/commercial/TunnelVentePage";
import InscriptionsPage from "./pages/commercial/InscriptionsPage";

// Trainer pages
import CreateCourse from "./pages/trainer/CreateCourse";
import CourseManager from "./pages/trainer/CourseManager";

// Admin pages
import AdminUsers from "./pages/admin/AdminUsers";
import EvaluationsAdminPage from "./pages/admin/EvaluationsAdminPage";
import RapportsPage from "./pages/admin/RapportsPage";
import AIQuizGeneratorPage from "./pages/admin/AIQuizGeneratorPage";
import SessionsArchivedPage from "./pages/admin/SessionsArchivedPage";
import BibliothequeArchivesPage from "./pages/admin/BibliothequeArchivesPage";
import ElearningSequencesPage from "./pages/admin/ElearningSequencesPage";
import ElearningActivitiesPage from "./pages/admin/ElearningActivitiesPage";
import ElearningParcoursPage from "./pages/admin/ElearningParcoursPage";
import ScormPage from "./pages/admin/ScormPage";
import ScormPlayer from "./pages/admin/ScormPlayer";
import SettingsPage from "./pages/admin/SettingsPage";
import ProgrammesPage from "./pages/admin/ProgrammesPage";
import ProgrammeViewPage from "./pages/admin/ProgrammeViewPage";
import FinancesDashboardPage from "./pages/admin/FinancesDashboardPage";
import ProgrammeEditPage from "./pages/admin/ProgrammeEditPage";
import SessionCreatePage from "./pages/admin/SessionCreatePage";

// Learner pages
import LearnerDashboard from "./pages/learner/LearnerDashboard";
import LearnerPlaceholder from "./pages/learner/LearnerPlaceholder";
import LearnerApprenantsPage from "./pages/learner/LearnerApprenantsPage";
import LearnerAProposPage from "./pages/learner/LearnerAProposPage";
import LearnerBadgesPage from "./pages/learner/LearnerBadgesPage";
import LearnerSuiviPage from "./pages/learner/LearnerSuiviPage";
import LearnerProgrammePage from "./pages/learner/LearnerProgrammePage";
import LearnerDocumentsPage from "./pages/learner/LearnerDocumentsPage";
import LearnerCertificatsPage from "./pages/learner/LearnerCertificatsPage";
import LearnerSequences from "./pages/learner/LearnerSequences";
import LearnerSequencePlayer from "./pages/learner/LearnerSequencePlayer";
import LearnerEmargements from "./pages/learner/LearnerEmargements";
import LearnerEvaluations from "./pages/learner/LearnerEvaluations";

// Components
import AuthCallback from "./components/AuthCallback";
import ProtectedRoute from "./components/ProtectedRoute";

// Smart redirect based on user role
const SmartRedirect = () => {
  const { user, loading } = useAuth();
  
  if (loading) return null;
  
  // If user is a student, redirect to learner interface
  if (user?.role === 'student') {
    return <Navigate to="/apprenant" replace />;
  }
  
  // Admin or trainer goes to admin dashboard
  return <Navigate to="/admin" replace />;
};

function AppRouter() {
  const location = useLocation();
  
  // CRITICAL: Check URL fragment for session_id synchronously during render
  if (location.hash?.includes('session_id=')) {
    return <AuthCallback />;
  }

  return (
    <Routes>
      {/* Public routes */}
      <Route path="/" element={<LandingPage />} />
      <Route path="/login" element={<LoginPage />} />
      <Route path="/reset-password" element={<ResetPasswordPage />} />
      
      {/* Smart redirect after login */}
      <Route path="/dashboard" element={<ProtectedRoute><SmartRedirect /></ProtectedRoute>} />
      
      {/* ==================== ADMIN/TRAINER INTERFACE ==================== */}
      
      {/* Admin Dashboard */}
      <Route path="/admin" element={<ProtectedRoute requireTrainer><Dashboard /></ProtectedRoute>} />
      <Route path="/admin/agenda" element={<ProtectedRoute requireTrainer><AgendaPage /></ProtectedRoute>} />
      
      {/* Gestion commerciale */}
      <Route path="/commercial/tunnel" element={<ProtectedRoute requireTrainer><TunnelVentePage /></ProtectedRoute>} />
      <Route path="/commercial/inscriptions" element={<ProtectedRoute requireTrainer><InscriptionsPage /></ProtectedRoute>} />
      
      {/* Sessions de formation */}
      <Route path="/sessions" element={<ProtectedRoute requireTrainer><SessionsPage /></ProtectedRoute>} />
      <Route path="/sessions/archived" element={<ProtectedRoute requireTrainer><SessionsArchivedPage /></ProtectedRoute>} />
      <Route path="/sessions/nouveau" element={<ProtectedRoute requireTrainer><SessionCreatePage /></ProtectedRoute>} />
      <Route path="/sessions/new" element={<ProtectedRoute requireTrainer><SessionCreatePage /></ProtectedRoute>} />
      <Route path="/sessions/:sessionId/edit" element={<ProtectedRoute requireTrainer><SessionCreatePage /></ProtectedRoute>} />
      <Route path="/sessions/:courseId" element={<ProtectedRoute requireTrainer><CourseManager /></ProtectedRoute>} />
      
      {/* E-learning Admin */}
      <Route path="/elearning" element={<ProtectedRoute requireTrainer><ElearningDashboard /></ProtectedRoute>} />
      <Route path="/elearning/parcours" element={<ProtectedRoute requireTrainer><ElearningParcoursPage /></ProtectedRoute>} />
      <Route path="/elearning/parcours/new" element={<ProtectedRoute requireTrainer><CreateCourse /></ProtectedRoute>} />
      <Route path="/elearning/parcours/:courseId" element={<ProtectedRoute requireTrainer><CourseManager /></ProtectedRoute>} />
      <Route path="/elearning/sequences" element={<ProtectedRoute requireTrainer><ElearningSequencesPage /></ProtectedRoute>} />
      <Route path="/elearning/activities" element={<ProtectedRoute requireTrainer><ElearningActivitiesPage /></ProtectedRoute>} />
      <Route path="/elearning/ai-quiz" element={<ProtectedRoute requireTrainer><AIQuizGeneratorPage /></ProtectedRoute>} />
      <Route path="/elearning/scorm" element={<ProtectedRoute requireTrainer><ScormPage /></ProtectedRoute>} />
      <Route path="/elearning/scorm/:scormId/play" element={<ProtectedRoute><ScormPlayer /></ProtectedRoute>} />
      
      {/* Bibliothèque */}
      <Route path="/bibliotheque/programmes" element={<ProtectedRoute requireTrainer><ProgrammesPage /></ProtectedRoute>} />
      <Route path="/bibliotheque/programmes/nouveau" element={<ProtectedRoute requireTrainer><ProgrammeEditPage /></ProtectedRoute>} />
      <Route path="/bibliotheque/programmes/:programmeId" element={<ProtectedRoute requireTrainer><ProgrammeViewPage /></ProtectedRoute>} />
      <Route path="/bibliotheque/programmes/:programmeId/edit" element={<ProtectedRoute requireTrainer><ProgrammeEditPage /></ProtectedRoute>} />
      <Route path="/bibliotheque/evaluations" element={<ProtectedRoute requireTrainer><EvaluationsAdminPage /></ProtectedRoute>} />
      <Route path="/bibliotheque/archives" element={<ProtectedRoute requireTrainer><BibliothequeArchivesPage /></ProtectedRoute>} />
      
      {/* Rapports */}
      <Route path="/rapports/activite" element={<ProtectedRoute requireTrainer><RapportsPage type="activite" /></ProtectedRoute>} />
      <Route path="/rapports/factures" element={<ProtectedRoute requireTrainer><FinancesDashboardPage /></ProtectedRoute>} />
      <Route path="/rapports/commercial" element={<ProtectedRoute requireTrainer><RapportsPage type="commercial" /></ProtectedRoute>} />
      <Route path="/rapports/bpf" element={<ProtectedRoute requireTrainer><RapportsPage type="bpf" /></ProtectedRoute>} />
      <Route path="/rapports/qualite" element={<ProtectedRoute requireTrainer><RapportsPage type="qualite" /></ProtectedRoute>} />
      <Route path="/rapports/amelioration" element={<ProtectedRoute requireTrainer><RapportsPage type="amelioration" /></ProtectedRoute>} />
      <Route path="/rapports/marketplace" element={<ProtectedRoute requireTrainer><RapportsPage type="marketplace" /></ProtectedRoute>} />
      
      {/* Données */}
      <Route path="/donnees" element={<ProtectedRoute requireTrainer><DonneesDashboardPage /></ProtectedRoute>} />
      <Route path="/donnees/entreprises" element={<ProtectedRoute requireTrainer><EntreprisesPage /></ProtectedRoute>} />
      <Route path="/donnees/entreprises/nouveau" element={<ProtectedRoute requireTrainer><EntrepriseDetailPage /></ProtectedRoute>} />
      <Route path="/donnees/entreprises/:entrepriseId" element={<ProtectedRoute requireTrainer><EntrepriseDetailPage /></ProtectedRoute>} />
      <Route path="/donnees/apprenants" element={<ProtectedRoute requireTrainer><ApprenantsPage /></ProtectedRoute>} />
      <Route path="/donnees/apprenants/nouveau" element={<ProtectedRoute requireTrainer><ApprenantDetailPage /></ProtectedRoute>} />
      <Route path="/donnees/apprenants/import" element={<ProtectedRoute requireTrainer><ApprenantsImportPage /></ProtectedRoute>} />
      <Route path="/donnees/apprenants/:apprenantId" element={<ProtectedRoute requireTrainer><ApprenantDetailPage /></ProtectedRoute>} />
      <Route path="/donnees/intervenants" element={<ProtectedRoute requireTrainer><IntervenantsPage /></ProtectedRoute>} />
      <Route path="/donnees/intervenants/nouveau" element={<ProtectedRoute requireTrainer><IntervenantDetailPage /></ProtectedRoute>} />
      <Route path="/donnees/intervenants/import" element={<ProtectedRoute requireTrainer><IntervenantsImportPage /></ProtectedRoute>} />
      <Route path="/donnees/intervenants/:intervenantId" element={<ProtectedRoute requireTrainer><IntervenantDetailPage /></ProtectedRoute>} />
      <Route path="/donnees/financeurs" element={<ProtectedRoute requireTrainer><FinanceursPage /></ProtectedRoute>} />
      <Route path="/donnees/financeurs/nouveau" element={<ProtectedRoute requireTrainer><FinanceurDetailPage /></ProtectedRoute>} />
      <Route path="/donnees/financeurs/:financeurId" element={<ProtectedRoute requireTrainer><FinanceurDetailPage /></ProtectedRoute>} />
      <Route path="/donnees/lieux" element={<ProtectedRoute requireTrainer><LieuxPage /></ProtectedRoute>} />
      <Route path="/donnees/lieux/nouveau" element={<ProtectedRoute requireTrainer><LieuDetailPage /></ProtectedRoute>} />
      <Route path="/donnees/lieux/:lieuId" element={<ProtectedRoute requireTrainer><LieuDetailPage /></ProtectedRoute>} />
      
      {/* Settings Admin */}
      <Route path="/settings" element={<ProtectedRoute requireTrainer><SettingsPage /></ProtectedRoute>} />
      
      {/* Configuration */}
      <Route path="/configuration/comptes-acces" element={<ProtectedRoute requireAdmin><ComptesAccesPage /></ProtectedRoute>} />
      <Route path="/configuration/comptes-acces/nouveau" element={<ProtectedRoute requireAdmin><CompteAccesDetailPage /></ProtectedRoute>} />
      <Route path="/configuration/comptes-acces/:accountId" element={<ProtectedRoute requireAdmin><CompteAccesDetailPage /></ProtectedRoute>} />
      <Route path="/configuration/modeles-documents" element={<ProtectedRoute requireTrainer><ModelesDocumentsPage /></ProtectedRoute>} />
      <Route path="/configuration/modeles-documents/:templateId" element={<ProtectedRoute requireTrainer><ModeleDetailPage /></ProtectedRoute>} />
      <Route path="/configuration/modeles-emails" element={<ProtectedRoute requireTrainer><ModelesDocumentsPage /></ProtectedRoute>} />
      <Route path="/configuration/modeles-emails/:templateId" element={<ProtectedRoute requireTrainer><ModeleDetailPage /></ProtectedRoute>} />
      <Route path="/configuration/audit-log" element={<ProtectedRoute requireAdmin><AuditLogPage /></ProtectedRoute>} />
      
      {/* Admin Users */}
      <Route path="/admin/users" element={<ProtectedRoute requireAdmin><AdminUsers /></ProtectedRoute>} />

      {/* ==================== LEARNER INTERFACE ==================== */}
      
      <Route path="/apprenant" element={<ProtectedRoute><SessionProvider><LearnerDashboard /></SessionProvider></ProtectedRoute>} />
      <Route path="/apprenant/sequences" element={<ProtectedRoute><SessionProvider><LearnerSequences /></SessionProvider></ProtectedRoute>} />
      <Route path="/apprenant/sequences/:seqId" element={<ProtectedRoute><SessionProvider><LearnerSequencePlayer /></SessionProvider></ProtectedRoute>} />
      <Route path="/apprenant/suivi" element={<ProtectedRoute><SessionProvider><LearnerSuiviPage /></SessionProvider></ProtectedRoute>} />
      <Route path="/apprenant/programme" element={<ProtectedRoute><SessionProvider><LearnerProgrammePage /></SessionProvider></ProtectedRoute>} />
      <Route path="/apprenant/apprenants" element={<ProtectedRoute><SessionProvider><LearnerApprenantsPage /></SessionProvider></ProtectedRoute>} />
      <Route path="/apprenant/emargements" element={<ProtectedRoute><SessionProvider><LearnerEmargements /></SessionProvider></ProtectedRoute>} />
      <Route path="/apprenant/evaluations" element={<ProtectedRoute><SessionProvider><LearnerEvaluations /></SessionProvider></ProtectedRoute>} />
      <Route path="/apprenant/documents" element={<ProtectedRoute><SessionProvider><LearnerDocumentsPage /></SessionProvider></ProtectedRoute>} />
      <Route path="/apprenant/certificats" element={<ProtectedRoute><SessionProvider><LearnerCertificatsPage /></SessionProvider></ProtectedRoute>} />
      <Route path="/apprenant/apropos" element={<ProtectedRoute><SessionProvider><LearnerAProposPage /></SessionProvider></ProtectedRoute>} />
      <Route path="/apprenant/badges" element={<ProtectedRoute><SessionProvider><LearnerBadgesPage /></SessionProvider></ProtectedRoute>} />
      
      {/* ==================== SHARED ROUTES ==================== */}
      
      {/* Course detail & Quiz - accessible to all */}
      <Route path="/course/:courseId" element={<CourseDetail />} />
      <Route path="/courses" element={<CourseCatalog />} />
      <Route path="/quiz/:quizId" element={<ProtectedRoute><QuizPage /></ProtectedRoute>} />
      <Route path="/certificates" element={<ProtectedRoute><CertificatesPage /></ProtectedRoute>} />

      {/* Fallback */}
      <Route path="*" element={<LandingPage />} />
    </Routes>
  );
}

function App() {
  return (
    <div className="App">
      <BrowserRouter>
        <ThemeProvider>
          <LanguageProvider>
            <AuthProvider>
              <AppRouter />
              <Toaster 
                position="top-right"
                toastOptions={{
                  style: {
                    background: '#0f172a',
                    color: '#fff',
                    border: 'none',
                  },
                }}
              />
            </AuthProvider>
          </LanguageProvider>
        </ThemeProvider>
      </BrowserRouter>
    </div>
  );
}

export default App;
